/**
 * Summary statistics for a single weekday within a plan version.
 */
export interface WeekdaySummary {
  /** Total capacity slots available (sum of shift capacities) */
  totalSlots: number;
  /** Number of filled assignment slots */
  filledSlots: number;
  /** Coverage percentage: (filledSlots / totalSlots) * 100 */
  coveragePercent: number;
  /** Total shift hours for this weekday */
  hours: number;
}

/**
 * Per-employee planned workload within a plan version template.
 *
 * A plan version is a recurring weekly template — it has no relation to
 * concrete calendar dates. This structure records how many minutes of
 * shift work each employee is assigned per template cycle.
 *
 * The solver can later combine this with date-specific balance data
 * (from `person_date_balances`) at the plan-instance level to prefer
 * employees with a low balance.
 */
export interface EmployeeBalanceImpact {
  /** The person (employee) UUID */
  personId: string;
  /** Total planned workload in minutes derived from shift-unit durations per template cycle */
  plannedMinutes: number;
}

/**
 * Pre-computed summary statistics for a plan version.
 * Calculated when a version is published and stored in the summary JSONB column.
 */
export interface PlanVersionSummary {
  /** Total capacity slots across all weekdays */
  totalSlots: number;
  /** Total filled assignment slots across all weekdays */
  filledSlots: number;
  /** Overall coverage percentage: (filledSlots / totalSlots) * 100 */
  coveragePercent: number;
  /** Total shift hours across all weekdays */
  totalHours: number;
  /** Per-weekday breakdown, keyed by "weekday:0" through "weekday:6" */
  weekdays: Record<string, WeekdaySummary>;
  /** Per-employee balance impact for all assigned employees */
  employeeBalances: EmployeeBalanceImpact[];
  /** ISO timestamp when the summary was calculated */
  calculatedAt: string;
}
